/**
 * Ferrier price conversion — bypass Hoppy DOM rewriting on PDP/cards.
 *
 * Hoppy targets `.money` (from shop money_format). That caused intermittent
 * double-conversion on PDPs (e.g. CA$607.81 → CA$4.71) when original_price
 * was recached from an already-converted string.
 *
 * Theme prices use `.ferrier-price[data-shop-amount]` (no `.money`).
 * We convert with Hoppy's public `futureBlink_convertAmount` API so rates
 * stay in sync with the currency switcher, without Hoppy mutating these nodes.
 */
(() => {
  const VARIANT_UPDATE = 'variant:update';
  const PRICE_SELECTOR = '[data-ferrier-price][data-shop-amount], .ferrier-price[data-shop-amount]';
  let lastCurrency = localStorage.getItem('currentCurrency') || '';
  let updateTimer = null;

  const shopCurrency = () =>
    (window.futureBlink_storeCurrency || window.futureBlink_primaryCurrency || 'INR').toUpperCase();

  const selectedCurrency = () =>
    (localStorage.getItem('currentCurrency') || shopCurrency()).toUpperCase();

  const formatShopFallback = (cents) => {
    const value = Number(cents) / 100;
    if (!Number.isFinite(value)) return '';
    return value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const convertCents = (cents) => {
    const major = Number(cents) / 100;
    if (!Number.isFinite(major)) return null;

    const from = shopCurrency();
    const to = selectedCurrency();

    if (typeof window.futureBlink_convertAmount === 'function') {
      const converted = window.futureBlink_convertAmount(major, from, { toCurrency: to });
      if (converted) return converted;
    }

    // Fallback before Hoppy boots, or same-currency.
    if (to === from) return formatShopFallback(cents);
    return formatShopFallback(cents);
  };

  const updatePriceNode = (el) => {
    if (!(el instanceof HTMLElement)) return;
    const cents = el.getAttribute('data-shop-amount');
    if (!cents) return;
    const display = convertCents(cents);
    if (!display) return;
    if (el.textContent !== display) el.textContent = display;
    // Ensure Hoppy never treats this node as a money target.
    el.classList.remove('money');
    el.removeAttribute('original_price');
    el.removeAttribute('data-processed');
  };

  const updateAllPrices = (root = document) => {
    root.querySelectorAll?.(PRICE_SELECTOR).forEach(updatePriceNode);
  };

  const scheduleUpdate = (delay = 30) => {
    if (updateTimer) clearTimeout(updateTimer);
    updateTimer = setTimeout(() => {
      updateTimer = null;
      updateAllPrices();
    }, delay);
  };

  const syncDropdownInteractivity = (dropdown) => {
    if (!(dropdown instanceof HTMLElement)) return;
    const opacity = Number.parseFloat(dropdown.style.opacity || '0');
    const isOpen = opacity > 0.5;
    dropdown.classList.toggle('is-open', isOpen);
    dropdown.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    dropdown.style.pointerEvents = isOpen ? 'auto' : 'none';
    dropdown.style.visibility = isOpen ? 'visible' : 'hidden';
  };

  const watchCurrencyDropdowns = () => {
    const bind = (dropdown) => {
      if (!(dropdown instanceof HTMLElement) || dropdown.dataset.hoppyLockBound === 'true') return;
      dropdown.dataset.hoppyLockBound = 'true';
      syncDropdownInteractivity(dropdown);
      const observer = new MutationObserver(() => syncDropdownInteractivity(dropdown));
      observer.observe(dropdown, { attributes: true, attributeFilter: ['style', 'class'] });
    };

    const scan = (root = document) => {
      root
        .querySelectorAll?.(
          '.dropdown.futureblink-currency-dropdown, .futureblink-currency-box .dropdown, .currency-switcher .dropdown'
        )
        .forEach(bind);
    };

    scan();

    const rootObserver = new MutationObserver((mutations) => {
      let needsPriceUpdate = false;
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches?.('.dropdown')) bind(node);
          scan(node);
          if (node.matches?.(PRICE_SELECTOR) || node.querySelector?.(PRICE_SELECTOR)) {
            needsPriceUpdate = true;
          }
        });
      }
      if (needsPriceUpdate) scheduleUpdate(40);
    });
    rootObserver.observe(document.documentElement, { childList: true, subtree: true });
  };

  const watchCurrencySelection = () => {
    document.addEventListener(
      'click',
      (event) => {
        const option =
          event.target instanceof Element
            ? event.target.closest(
                '.futureblink-currency-dropdown .option, .currency-switcher .dropdown .option, .dropdown.futureblink-currency-dropdown .option'
              )
            : null;
        if (!option) return;
        // Hoppy sets localStorage asynchronously — refresh a few times.
        scheduleUpdate(20);
        setTimeout(() => scheduleUpdate(0), 100);
        setTimeout(() => scheduleUpdate(0), 300);
      },
      true
    );

    const checkCurrency = () => {
      const current = localStorage.getItem('currentCurrency') || '';
      if (current === lastCurrency) return;
      lastCurrency = current;
      scheduleUpdate(20);
      setTimeout(() => scheduleUpdate(0), 150);
    };

    window.addEventListener('storage', (event) => {
      if (event.key === 'currentCurrency') checkCurrency();
    });
    setInterval(checkCurrency, 400);
  };

  // Wait until Hoppy exposes convertAmount (script is deferred).
  const waitForHoppy = () => {
    let tries = 0;
    const tick = () => {
      tries += 1;
      if (typeof window.futureBlink_convertAmount === 'function' || tries > 40) {
        updateAllPrices();
        return;
      }
      setTimeout(tick, 100);
    };
    tick();
  };

  document.addEventListener(VARIANT_UPDATE, () => {
    requestAnimationFrame(() => scheduleUpdate(40));
  });

  const boot = () => {
    watchCurrencyDropdowns();
    watchCurrencySelection();
    updateAllPrices();
    waitForHoppy();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
